import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare, Send, Loader2, MessageCircle } from 'lucide-react';
import { messaging } from '@/lib/api';
import { toast } from 'sonner';

/**
 * MatterMessagingCard — unified SMS/WhatsApp inbox scoped to a single matter.
 * Every inbound message from the client's registered phone that Twilio/Meta
 * routes into the org lands here (already stored via messaging_router
 * inbound webhooks). Staff can reply outbound from the same thread so the
 * conversation stays inside the case file.
 */
export default function MatterMessagingCard({ matterRef, applicantPhone }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [reply, setReply] = React.useState('');
  const [channel, setChannel] = React.useState('sms');
  const [sending, setSending] = React.useState(false);
  const endRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const r = await messaging.thread(matterRef); setItems(r.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, [matterRef]);

  React.useEffect(() => { if (matterRef) load(); }, [matterRef, load]);
  React.useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [items]);

  const send = async () => {
    if (!reply.trim()) return;
    if (!applicantPhone) { toast.error('No phone number on file for this applicant'); return; }
    setSending(true);
    try {
      await messaging.send(applicantPhone, channel, reply.trim(), matterRef);
      setReply('');
      toast.success('Message sent');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <Card data-testid="matter-messaging-card"><CardContent className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2ECC9A]/10">
            <MessageSquare className="h-4 w-4 text-[#2ECC9A]" />
          </div>
          <div>
            <div className="text-sm font-semibold text-[#1A3A5C]">Messages · SMS & WhatsApp</div>
            <div className="text-[11px] text-slate-500">
              {applicantPhone ? `Threading with ${applicantPhone}` : 'No client phone on file'}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500">{items.length} messages</div>
      </div>

      <div className="mb-3 max-h-72 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/40 p-3" data-testid="matter-messaging-thread">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-[#2ECC9A]" /></div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500">
            <MessageCircle className="mx-auto mb-2 h-6 w-6 text-slate-300" />
            No messages yet. Send one below to start the thread.
          </div>
        ) : (
          items.map((msg) => {
            const isOut = msg.direction === 'outbound';
            return (
              <div key={msg.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`} data-testid={`matter-msg-${msg.id}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                  isOut ? 'bg-[#1A3A5C] text-white' : 'bg-white border border-slate-200 text-[#1A3A5C]'}`}>
                  <div className="whitespace-pre-wrap">{msg.body}</div>
                  <div className={`mt-1 text-[10px] ${isOut ? 'text-[#2ECC9A]' : 'text-slate-400'}`}>
                    {msg.channel?.toUpperCase()} · {new Date(msg.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-2">
        <select value={channel} onChange={(e) => setChannel(e.target.value)}
                className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs"
                data-testid="matter-messaging-channel">
          <option value="sms">SMS</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <Input placeholder={applicantPhone ? `Message ${applicantPhone}…` : 'Add phone on applicant first'}
               value={reply} onChange={(e) => setReply(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
               disabled={!applicantPhone}
               data-testid="matter-messaging-input" />
        <Button size="sm" className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"
                onClick={send} disabled={sending || !applicantPhone || !reply.trim()}
                data-testid="matter-messaging-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </CardContent></Card>
  );
}
